/**
 * Shared "is this a brand-new sign-up, and have we already reported it?"
 * logic, used by both the Google Ads conversion (`./gtag.ts`) and the
 * PostHog `signup_completed` event.
 *
 * Both reporters run from the authenticated shell rather than from the
 * sign-up form, because Google OAuth full-page-redirects past any form
 * callback — the shell is the only place that catches both auth flows. That
 * means they mount on every page load, so each needs a freshness window and
 * a once-per-browser guard or it would re-fire indefinitely.
 */

/**
 * A user counts as a fresh sign-up while their account is younger than this.
 * Wide enough to survive a slow onboarding, narrow enough that a returning
 * sign-in on a second device days later never re-fires.
 */
export const NEW_USER_WINDOW_MS = 30 * 60 * 1000

export const isFreshSignUp = (creationTime: number): boolean =>
  Date.now() - creationTime <= NEW_USER_WINDOW_MS

/**
 * Claims the one-shot for `userId` under `storageKey`, returning `true` the
 * first time and `false` afterwards.
 *
 * Returns `true` when storage is unavailable (private mode): re-reporting
 * occasionally is the lesser failure compared with never reporting at all.
 * Each caller passes its own key so the two reporters can't consume each
 * other's one-shot.
 */
export const claimSignUpReport = (storageKey: string, userId: string): boolean => {
  try {
    if (localStorage.getItem(storageKey) === userId) {
      return false
    }
    localStorage.setItem(storageKey, userId)
  } catch {
    // localStorage unavailable — fall through and report.
  }
  return true
}
